import React, { useContext } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useMediaQuery } from 'react-responsive'
import { useSelector } from 'react-redux'

import { holidayGlobalAlertId } from 'utils/promoThemes/holiday/appBanner'
import { Mixpanel, MIXPANEL_PROPS } from 'utils/mixpanel'
import { isDomain } from 'utils/url-validator'
import { OnboardingContext } from 'features/onboarding/context'

import {
  fonts,
  mediaQueries,
  palette,
  setOpacity,
} from '../../styles/variables'
import { pushWithLanguageRoutes, translate } from '../../utils/translation'
import { Text } from '../../components/topography'
import SettingsSelector from '../../components/selector/SettingsSelector'
import Icon, { ArrowLabel } from '../../components/icon/Icon'
import welcome from '../../styles/assets/alerts/welcome.svg'
import notification from '../../styles/assets/alerts/notification.svg'
import opportunity from '../../styles/assets/alerts/opportunity.svg'
import opportunityOrange from '../../styles/assets/alerts/opportunity_orange.svg'
import prepared from '../../styles/assets/alerts/prepared.svg'
import congratulations from '../../styles/assets/alerts/congratulations.svg'
import audit from '../../styles/assets/alerts/audit.svg'
import auditGreen from '../../styles/assets/alerts/audit_green.svg'
import goodwork from '../../styles/assets/alerts/goodwork.svg'
import opps from '../../styles/assets/alerts/opps.svg'
import progress from '../../styles/assets/alerts/progress.svg'
import payment_failed from '../../styles/assets/alerts/payment_failed.svg'
import payment_success from '../../styles/assets/alerts/payment_success.svg'
import preparedGreen from '../../styles/assets/alerts/prepared_green.svg'
import checkIcon from '../../styles/assets/alerts/check.svg'
import checkIconHover from '../../styles/assets/alerts/check_orange.svg'
import removeIcon from '../../styles/assets/alerts/remove.svg'
import removeIconHover from '../../styles/assets/alerts/remove_orange.svg'
import treeDots from '../../styles/assets/alerts/three_dots.svg'
import openedPresent from '../../styles/assets/alerts/opened_present.svg'
import { CloseButton, PrimaryButton } from '../../components/buttons/Buttons'
import { openInNewTab, formatChartDate, getHash } from '../../utils/functions'
import { RecurlyErrorCodes } from '../../utils/payment'
import { Image } from '../Pricing/styled'
import clockIcon from '../../styles/assets/alerts/clock.svg'
import meterIcon from '../../styles/assets/alerts/meter.svg'
import graphIcon from '../../styles/assets/alerts/graph.svg'
import bookIcon from '../../styles/assets/alerts/book.png'
import playIcon from '../../styles/assets/alerts/play.svg'
import { Desktop, Mobile } from '../../utils/media'
import { selectProjects } from '../Dashboard/selectors'

const icons = {
  welcome_alert: [welcome, welcome],
  global_alert: [notification, notification],
  keyword_seasonality: [opportunity, prepared],
  position_tracking: [congratulations, congratulations],
  site_audit: [audit, audit],
  payment_error_alert: [payment_failed, payment_failed],
  payment_success_alert: [payment_success, payment_success],
  site_audit_report_created: [auditGreen, auditGreen],
}

export const AddButtonWrapper = styled.div({
  [mediaQueries.mobile]: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    [mediaQueries.mobile]: {
      flexDirection: 'column',
      '& button': {
        height: '40px',
        lineHeight: '40px',
        '& + button': {
          marginLeft: 0,
          marginTop: '8px',
        },
      },
    },
  },
})

const OuterWrapper = styled.div((props) => ({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: `1px solid ${palette.lightGray50}`,
  background: props.unread ? palette.gray10 : palette.transparent,
  '&:hover': {
    background:
      props.hoverEffect || props.unread ? palette.gray10 : palette.transparent,

    '& .alert-icon': {
      background: palette.orange10,
    },
  },
  [mediaQueries.mobile]: {
    marginBottom: '5px',
  },
}))

const AlertWrapper = styled.div(({ hoverEffect, unread, hasOuter }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  borderBottom: hasOuter ? 'none' : `1px solid ${palette.lightGray50}`,
  background: unread ? palette.gray10 : palette.transparent,
  '&:hover': {
    background: (() => {
      if (hoverEffect) {
        return palette.gray10
      }

      return unread ? palette.gray10 : palette.transparent
    })(),
    '& .alert-icon': {
      background: palette.orange10,
    },
  },
}))

const Wrapper = styled.div({
  display: 'flex',
  margin: '15px',
  [mediaQueries.mobile]: {
    margin: '15px 8px',
  },
})

const AlertIconCircle = styled.div((props) => ({
  width: '56px',
  minWidth: '56px',
  height: '56px',
  background: props.background || palette.lightGray20,
  borderRadius: '28px',
  padding: '14px',
  textAlign: 'center',
  [mediaQueries.mobile]: {
    width: '48px',
    height: '48px',
    minWidth: '48px',
    padding: '10px',
  },
}))

export const AlertIcon = ({ background, icon, iconMargin }) => {
  return (
    <AlertIconCircle background={background}>
      <Icon src={icon} margin={iconMargin} />
    </AlertIconCircle>
  )
}

const Alert = styled.div({
  margin: '0px 30px 0 15px',
  flex: 1,
  [mediaQueries.mobile]: {
    margin: '0px 15px',
  },
})

const AlertTime = ({ time }) => {
  const alertDate = moment(new Date(time))
  const today = moment()

  if (today.diff(alertDate, 'days') === 0) {
    return (
      <Text fontSize="14px" lineHeight="22px" color={palette.gray}>
        {translate('n_hours_ago', today.diff(alertDate, 'hours'))}
      </Text>
    )
  }
  return (
    <div>
      <Text
        fontSize="14px"
        fontFamily={fonts.primaryMedium}
        lineHeight="22px"
        color={palette.darkGray}
      >
        {alertDate.format('DD MMM')}
      </Text>
      <Text fontSize="14px" lineHeight="22px" color={palette.gray}>
        {translate('n_days_ago', today.diff(alertDate, 'days'))}
      </Text>
    </div>
  )
}

const UnReadDot = styled.div({
  width: '8px',
  minWidth: '8px',
  height: '8px',
  background: palette.blue,
  borderRadius: '4px',
  marginTop: '25px',
})

export const getAlertTextandIcon = (alert, prefix = '', isMobile = false) => {
  if (!alert) {
    return {}
  }

  const { content, alertId } = alert
  const index = getHash(alertId) % 2
  let heading = translate('menu_general')
  let textId = ''
  let icon = icons[alert.alertType]
    ? icons[alert.alertType][index % 2]
    : notification
  let iconBg = palette.danger
  let text = ''
  let actionText
  let url = 'https://knowledgebase.neilpatel.com/in-app-alerts/'
  let buttonText = translate('get_started')
  let pushState
  let triggerNo

  switch (alert.alertType) {
    case 'keyword_seasonality':
      heading = translate('alert_keyword_opportunities')
      textId =
        index % 2 === 0
          ? `${prefix}keyword_seasonality_text_a`
          : `${prefix}keyword_seasonality_text_b`
      text = translate(
        textId,
        content.keyword,
        alert.projectName,
        content.variationPercentage,
        formatChartDate(content.dateStart, 'YYYYMM', 'MMMM'),
        content.variationAbsolute,
      )
      icon = opportunity
      iconBg = palette.success
      break
    case 'position_tracking': {
      heading = `<b>${alert.projectName}</b> | ${translate('rank_tracking')}`
      if (content.report_type) {
        buttonText = translate('see_how_button')
        url = `/alerts/${alert.alertId}`
        text = translate(
          `position_tracking_alert_${content.report_type}_variation${
            1 + (index % 2)
          }`,
          content.keywords_total,
          alert.projectName,
        )
        actionText = translate(
          `position_tracking_alert_action_text_${content.report_type}`,
        )
        if (content.report_type === 'climbed_10') {
          icon = congratulations
          iconBg = palette.success
          buttonText = translate('see_how_button')
          triggerNo = 'trigger01'
        } else if (content.report_type === 'improved') {
          icon = goodwork
          iconBg = palette.success
          buttonText = translate('show_me_how_button')
          triggerNo = 'trigger03'
        } else if (content.report_type === 'dropped') {
          icon = opps
          iconBg = palette.warning
          buttonText = translate('show_me_how_button')
          triggerNo = 'trigger04'
        } else {
          icon = progress
          iconBg = palette.warning
          buttonText = translate('learn_how_button')
          triggerNo = 'trigger02'
        }
      } else {
        const { improved } = content.overview
        const { dropped } = content.overview
        const { started } = content.overview
        const { stopped } = content.overview
        const climbed10 = content.overview.climbed_10
        const dropped10 = content.overview.dropped_10

        if (climbed10 > 0 || dropped10 > 0) {
          if (dropped10 > climbed10) {
            text = translate(
              'position_tracking_alert_text_b',
              dropped10,
              alert.projectName,
            )
            icon = opps
            iconBg = palette.warning
          } else {
            text = translate(
              'position_tracking_alert_text_a',
              climbed10,
              alert.projectName,
            )
            icon = congratulations
            iconBg = palette.success
          }
        } else if (stopped > 0 || stopped > 0) {
          if (stopped > started) {
            text = translate(
              'position_tracking_alert_text_d',
              stopped,
              alert.projectName,
            )
            icon = opps
            iconBg = palette.warning
          } else {
            text = translate(
              'position_tracking_alert_text_c',
              started,
              alert.projectName,
            )
            icon = goodwork
            iconBg = palette.success
          }
        } else if (improved > 0 || dropped > 0) {
          if (dropped > improved) {
            text = translate(
              'position_tracking_alert_text_g',
              dropped,
              alert.projectName,
            )
            icon = opps
            iconBg = palette.warning
          } else {
            text = translate(
              'position_tracking_alert_text_e',
              improved,
              alert.projectName,
            )
            icon = goodwork
            iconBg = palette.success
          }
        } else {
          text = translate('position_tracking_alert_text_f', alert.projectName)
        }
      }
      break
    }
    case 'site_audit': {
      heading = `<b>${alert.projectName}</b> | ${translate(
        'menu_seo_analyzer',
      )}`
      // actionText = translate('site_audit_alert_subtext')
      buttonText = translate('get_started')
      // url = `/alerts/${alert.alertId}`
      // pushState = { alert }

      if (content.report_type) {
        buttonText = translate('see_how_button')
        url = `/alerts/${alert.alertId}`

        if (content.report_type === 'all_fixed_no_new') {
          text = translate(
            `site_audit_alert_all_fixed_no_new_variation${1 + (index % 2)}`,
            alert.projectName,
          )
          actionText = translate(
            `site_audit_alert_action_text_all_fixed_no_new`,
          )
          icon = congratulations
          iconBg = palette.success
          buttonText = translate('learn_more')
          triggerNo = 'trigger01'
        } else if (content.report_type === 'all_fixed_new') {
          text = translate(
            `site_audit_alert_all_fixed_new_variation${1 + (index % 2)}`,
            alert.projectName,
          )
          actionText = translate(`site_audit_alert_action_text_all_fixed_new`)
          icon = goodwork
          iconBg = palette.success
          buttonText = isMobile
            ? translate('see_details')
            : translate('see_opportunities_button')
          triggerNo = 'trigger02'
        } else if (content.report_type === 'some_fixed_no_new') {
          text = translate(
            `site_audit_alert_some_fixed_no_new_variation1`,
            alert.projectName,
          )
          actionText = translate(
            `site_audit_alert_action_text_some_fixed_no_new`,
          )
          icon = opportunity
          iconBg = palette.success
          buttonText = isMobile
            ? translate('see_details')
            : translate('see_opportunities_button')
          triggerNo = 'trigger03'
        } else if (content.report_type === 'some_fixed_new') {
          text = translate(
            `site_audit_alert_some_fixed_no_new_variation1`,
            alert.projectName,
          )
          actionText = translate(`site_audit_alert_action_text_some_fixed_new`)
          icon = audit
          iconBg = palette.warning
          buttonText = isMobile
            ? translate('see_details')
            : translate('see_opportunities_button')
          triggerNo = 'trigger04'
        } else if (content.report_type === 'none_fixed_no_new') {
          text = translate(
            `site_audit_alert_none_fixed_no_new_variation${1 + (index % 2)}`,
            alert.projectName,
          )
          actionText = translate(
            `site_audit_alert_action_text_none_fixed_no_new`,
            alert.projectName,
          )
          icon = audit
          iconBg = palette.warning
          buttonText = isMobile
            ? translate('see_details')
            : translate('see_opportunities_button')
          triggerNo = 'trigger05'
        } else if (content.report_type === 'none_fixed_new') {
          text = translate(
            `site_audit_alert_none_fixed_no_new_variation${1 + (index % 2)}`,
            alert.projectName,
          )
          actionText = translate(
            `site_audit_alert_action_text_none_fixed_no_new`,
            alert.projectName,
          )
          icon = audit
          iconBg = palette.warning
          buttonText = isMobile
            ? translate('see_details')
            : translate('see_opportunities_button')
          triggerNo = 'trigger06'
        }
      } else {
        const total = content.issues.total.value
        const newErrors = content.issues.new_errors
        const errorsFixed = content.issues.errors_fixed

        if (newErrors === 0) {
          if (total === newErrors) {
            text = translate('site_audit_alert_text_a', alert.projectName)
            icon = congratulations
            iconBg = palette.success
          } else if (errorsFixed > 0) {
            text = translate('site_audit_alert_text_b', alert.projectName)
            icon = opportunity
            iconBg = palette.success
          } else if (errorsFixed === 0) {
            text = translate('site_audit_alert_text_c', alert.projectName)
            icon = audit
            iconBg = palette.warning
          }
        } else if (total === newErrors) {
          text = translate('site_audit_alert_text_e', alert.projectName)
          icon = audit
          iconBg = palette.warning
        } else if (errorsFixed > 0) {
          text = translate('site_audit_alert_text_e', alert.projectName)
          icon = goodwork
          iconBg = palette.success
        } else if (errorsFixed === 0) {
          text = translate('site_audit_alert_text_d', alert.projectName)
          icon = audit
          iconBg = palette.warning
        }
      }
      break
    }
    case 'global_alert':
      text =
        prefix === 'popup_' && content.popup_text
          ? content.popup_text
          : content.text
      url = content.url
      buttonText = content.btn_text
        ? content.btn_text
        : translate('view_details')
      icon = openedPresent
      iconBg = palette.info
      break
    case 'welcome_alert':
      text = translate(`${prefix}welcome_alert`)
      url =
        'https://knowledgebase.neilpatel.com/what-is-ubersuggest-getting-started-guide/'
      icon = welcome
      iconBg = palette.info
      break
    case 'payment_error_alert': {
      const error =
        RecurlyErrorCodes.find((e) => e === content.error_code) || 'other'
      text = translate(
        'payment_error_alert',
        translate(`payment_error_${error}`),
      )
      buttonText = translate('update_payment_method')
      url = '/settings/account_billing'
      pushState = { action: 'upgrade' }
      iconBg = palette.danger
      break
    }
    case 'payment_success_alert':
      text = translate('payment_success_alert')
      buttonText = translate('view_invoice')
      url = content ? content.url : ''
      iconBg = palette.success
      break
    case 'site_audit_report_created':
      heading = `<b>${alert.content.domain}</b> | ${translate(
        'menu_seo_analyzer',
      )}`
      text = translate('site_audit_alert_complete_status', alert.content.domain)
      actionText = translate('site_audit_alert_complete_status_action_text')
      buttonText = translate('show_me_results_button')
      url = `/seo_analyzer/site_audit?domain=${alert.content.domain}&action=crawl`
      iconBg = palette.success
      break
    case 'project_update_completed':
      heading = `<b>${alert.projectName}</b> | ${translate('dashboard')}`
      text = translate('alert_project_update', alert.projectName)
      buttonText = translate('results_button')
      url = `/dashboard`
      iconBg = palette.success
      icon = preparedGreen
      pushState = { projectId: alert.projectId }
      break
    case 'onboarding_survey_alert':
      text = translate('onboarding_questionnaire_alert')
      icon = notification
      iconBg = palette.orange20
      break
    case 'keywords_setup_alert':
      text = translate('complete_setup_alert_keywords', alert.projectName || '')
      icon = opportunityOrange
      iconBg = palette.orange20
      url = `/dashboard?projectId=${alert.projectId}&action=open_keywords_modal`
      break
    case 'competitors_setup_alert':
      text = translate(
        'complete_setup_alert_competitors',
        alert.projectName || '',
      )
      icon = opportunityOrange
      iconBg = palette.orange20
      url = `/dashboard?projectId=${alert.projectId}&action=open_competitors_modal`
      break
    default:
      text = translate(`unknown alert`)
  }
  return {
    heading,
    text,
    actionText,
    icon,
    iconBg: setOpacity(iconBg, 0.25),
    url,
    buttonText,
    pushState,
    triggerNo,
  }
}

export const AlertList = ({ alerts, ...props }) => {
  return alerts.map((alert) => {
    const { text, icon, iconBg } = getAlertTextandIcon(alert, 'popup_')
    return (
      <AlertWrapper
        key={alert.alertId}
        hoverEffect
        style={{ width: '350px', margin: '0 -13px', cursor: 'pointer' }}
        onClick={() => props.onClick(alert)}
      >
        <Wrapper
          style={{
            flex: 1,
            margin: '15px 13px',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <>
            <AlertIcon icon={icon} background={iconBg} />
            <Alert>
              <Text
                lineHeight="22px"
                dangerouslySetInnerHTML={{
                  __html: text,
                }}
                boldColor={palette.darkGray}
                cursor="pointer"
              />
            </Alert>
          </>
          {!alert.seen && <UnReadDot />}
        </Wrapper>
      </AlertWrapper>
    )
  })
}

export const SiteAuditReportAlert = ({ alert, ...props }) => {
  if (!alert) return null
  const { text, icon, iconBg } = getAlertTextandIcon(alert, 'popup_')
  return (
    <AlertWrapper
      style={{
        width: '350px',
        margin: '0 -13px',
        cursor: 'pointer',
        borderBottom: 'none',
      }}
    >
      <CloseButton
        onClick={props.onClose}
        styles={{
          width: '16px',
          height: '16px',
          right: '8px',
          top: '8px',
          ':before': {
            content: `''`,
            borderLeft: `1px solid ${props.color || palette.pureBlack}`,
            height: props.size || '11px',
            transform: 'rotate(45deg)',
          },
          ':after': {
            content: `''`,
            borderLeft: `1px solid ${props.color || palette.pureBlack}`,
            height: props.size || '11px',
            transform: 'rotate(-45deg)',
            marginLeft: '-1px',
          },
        }}
      />
      <Wrapper
        style={{
          flex: 1,
          margin: '5px 13px',
          display: 'flex',
          justifyContent: 'space-between',
        }}
        onClick={() => props.onClick(alert)}
      >
        <>
          <AlertIcon icon={icon} background={iconBg} />
          <Alert>
            <Text
              lineHeight="22px"
              dangerouslySetInnerHTML={{
                __html: text,
              }}
              boldColor={palette.darkGray}
            />
          </Alert>
        </>
        {!alert.seen && <UnReadDot style={{ marginTop: '45px' }} />}
      </Wrapper>
    </AlertWrapper>
  )
}

export const AlertCard = ({ alert, index, ...props }) => {
  const isMobile = useMediaQuery({ maxWidth: 767 })
  const onboarding = useContext(OnboardingContext)

  const {
    heading,
    text,
    actionText,
    icon,
    iconBg,
    url,
    pushState,
    buttonText,
  } = getAlertTextandIcon(alert, '', isMobile)
  const projects = useSelector(selectProjects())
  const { seen } = alert

  const renderButton = () => (
    <PrimaryButton
      theme="orange"
      tag={buttonText}
      mobileWidth="100%"
      style={{
        marginTop: '8px',
        height: '40px',
        lineHeight: '32px',
      }}
      onClick={() => {
        if (alert.content?.global_alert_id === holidayGlobalAlertId) {
          Mixpanel.saveLastPurchaseButtonLocation(
            MIXPANEL_PROPS.PURCHASE_BUTTON_LOCATION.values.HOLIDAY_ALERT_CTA,
          )
          pushWithLanguageRoutes('/pricing', pushState)
          return
        }

        if (alert.alertType === 'onboarding_survey_alert') {
          onboarding.openModal('notification')
          return
        }

        if (isDomain(url)) {
          openInNewTab(url)
        } else if (url) {
          if (alert.alertType === 'site_audit_report_created') {
            props.markNotification([alert], true)
          } else if (
            alert.alertType === 'project_update_completed' &&
            projects &&
            projects.length > 0
          ) {
            const project = projects.find((p) => p.id === alert.projectId)
            if (project) props.changeCurrentProject(project)
          }
          pushWithLanguageRoutes(url, pushState)
        }
      }}
    />
  )

  return (
    <OuterWrapper key={index} unread={!alert.seen}>
      <AlertWrapper key={index} hasOuter>
        <Wrapper style={{ flex: 1 }}>
          <AlertIcon icon={icon} background={iconBg} />
          <Alert>
            <Text
              fontSize="12px"
              lineHeight="12px"
              dangerouslySetInnerHTML={{
                __html: heading,
              }}
              color={palette.darkGray50}
              boldColor={palette.darkGray50}
              margin="0 0 8px 0"
            />
            <Text
              lineHeight="22px"
              dangerouslySetInnerHTML={{
                __html: text,
              }}
              boldColor={palette.darkGray}
            />
            {actionText && (
              <Text
                fontSize="12px"
                lineHeight="22px"
                dangerouslySetInnerHTML={{
                  __html: actionText,
                }}
                boldColor={palette.darkGray}
                margin="0 0 8px 0"
              />
            )}
            <Desktop>{renderButton()}</Desktop>
            <Mobile>
              <AlertTime time={alert.date} />
            </Mobile>
          </Alert>
          <Desktop>
            <AlertTime time={alert.date} />
          </Desktop>
        </Wrapper>
        <Wrapper>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignContent: 'space-between',
              justifyContent: alert.seen ? 'flex-start' : 'space-between',
            }}
          >
            <SettingsSelector
              options={[
                {
                  icons: [checkIcon, checkIconHover],
                  text: seen
                    ? translate('mark_as_unread')
                    : translate('mark_as_read'),
                  description: seen
                    ? translate('mark_as_unread_description')
                    : translate('mark_as_read_description'),
                  onClick: () => props.markNotification([alert], !seen),
                },
                {
                  icons: [removeIcon, removeIconHover],
                  text: translate('remove_this'),
                  description: translate('remove_this_notification'),
                  onClick: () => props.removeNotification(alert),
                },
              ]}
              style={{ marginLeft: '20px' }}
              icon={treeDots}
            />
            {!seen && <UnReadDot style={{ marginLeft: '20px' }} />}
          </div>
        </Wrapper>
      </AlertWrapper>
      <Mobile>{renderButton()}</Mobile>
    </OuterWrapper>
  )
}

export const AlertInfo = ({ alert, index, ...props }) => {
  const { text, icon, iconBg } = getAlertTextandIcon(alert)
  const { seen, content } = alert
  const alertDate = moment.utc(alert.date)
  const fromDate = moment.utc(content.project.from_date)
  const toDate = moment.utc(content.project.to_date)
  const today = moment.utc()
  let diffText = ''

  if (today.diff(alertDate, 'days') === 0) {
    diffText = translate('n_hours_ago', today.diff(alertDate, 'hours'))
  } else {
    diffText = translate('n_days_ago', today.diff(alertDate, 'days'))
  }

  return (
    <AlertWrapper
      key={index}
      unread={!alert.seen}
      style={{ background: 'transparent', borderBottom: 'none' }}
    >
      <Wrapper style={{ flex: 1, margin: 0 }}>
        <AlertIcon icon={icon} background={iconBg} />
        <Alert
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Text
            lineHeight="22px"
            dangerouslySetInnerHTML={{
              __html: text,
            }}
            boldColor={palette.darkGray}
          />
          <Wrapper
            style={{
              margin: 0,
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
            }}
          >
            <Text
              fontFamily={fonts.primaryMedium}
              fontSize="14px"
              lineHeight="22px"
              color={palette.darkGray}
            >
              {`${alertDate.format('DD')} ${translate(
                alertDate.format('MMMM').toLowerCase(),
              )} ${alertDate.format('YYYY')}`}
            </Text>
            <Text
              fontSize="14px"
              lineHeight="14px"
              color={palette.gray}
              style={{ margin: '0 8px' }}
            >
              {`|  ${diffText}  |`}
            </Text>
            <Text fontSize="14px" lineHeight="22px" color={palette.darkGray}>
              {`${fromDate.format('DD')} ${translate(
                fromDate.format('MMMM').toLowerCase(),
              )} ${fromDate.format('YYYY')} - ${toDate.format(
                'DD',
              )} ${translate(
                toDate.format('MMMM').toLowerCase(),
              )} ${toDate.format('YYYY')}`}
            </Text>
          </Wrapper>
        </Alert>
      </Wrapper>
      <Wrapper style={{ margin: 0 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignContent: 'space-between',
            justifyContent: alert.seen ? 'flex-start' : 'space-between',
          }}
        >
          <SettingsSelector
            options={[
              {
                icons: [checkIcon, checkIconHover],
                text: seen
                  ? translate('mark_as_unread')
                  : translate('mark_as_read'),
                description: seen
                  ? translate('mark_as_unread_description')
                  : translate('mark_as_read_description'),
                onClick: () => props.markNotification([alert], !seen),
              },
              {
                icons: [removeIcon, removeIconHover],
                text: translate('remove_this'),
                description: translate('remove_this_notification'),
                onClick: () => props.removeNotification(alert),
              },
            ]}
            style={{ marginLeft: '20px' }}
            icon={treeDots}
          />
        </div>
      </Wrapper>
    </AlertWrapper>
  )
}

export const Header = styled.div({
  display: 'flex',
  margin: '10px 0',
  [mediaQueries.mobile]: {
    flexWrap: 'wrap',
    flexDirection: 'column',
  },
})

export const Row = styled.div({
  display: 'flex',
  marginTop: '24px',
  [mediaQueries.mobile]: {
    flexDirection: 'column',
  },
})

export const Box = styled.div(() => ({
  display: 'flex',
  flexDirection: 'column',
  border: `1px solid ${palette.lightGray50}`,
  backgroundColor: palette.white,
  flex: '1',
  maxWidth: '360px',
  position: 'relative',
  '&:nth-child(1n+2)': {
    marginLeft: '30px',
    [mediaQueries.mobile]: {
      marginLeft: 0,
      marginTop: '40px',
    },
  },
  [mediaQueries.mobile]: {
    justifyContent: 'center',
    width: '100%',
  },
}))

export const ColorLabel = styled.span(({ type }) => {
  const borderColor = {
    warning: palette.warning,
    good: palette.success,
    error: palette.danger,
  }
  const bgColor = {
    warning: setOpacity(palette.warning, 0.4),
    good: setOpacity(palette.success, 0.4),
    error: setOpacity(palette.danger, 0.4),
  }
  return {
    fontFamily: fonts.primaryMedium,
    padding: '5px',
    textTransform: 'uppercase',
    color: palette.darkGray,
    fontSize: '12px',
    lineHeight: '12px',
    border: `1px solid ${borderColor[type]}`,
    background: bgColor[type],
    borderRadius: '2px',
    margin: '4px',
  }
})

const getDifficultyLabel = (difficulty) => {
  return (
    {
      medium: {
        type: 'warning',
        text: translate('medium'),
      },
      easy: {
        type: 'good',
        text: translate('easy'),
      },
      'easy-medium': {
        type: 'medium',
        text: `${translate('easy')}-${translate('medium')}`,
      },
      'medium-hard': {
        type: 'warning',
        text: `${translate('medium')}-${translate('hard')}`,
      },
    }[difficulty] || {
      type: 'error',
      text: `${translate('easy')}-${translate('hard')}`,
    }
  )
}

const getSEOImpactLabel = (seoImpact) => {
  return (
    {
      medium: {
        type: 'warning',
        text: translate('medium'),
      },
      high: {
        type: 'good',
        text: translate('high'),
      },
      low: {
        type: 'error',
        text: translate('low'),
      },
      'low-medium': {
        type: 'warning',
        text: `${translate('low')}-${translate('medium')}`,
      },
      'medium-high': {
        type: 'good',
        text: `${translate('medium')}-${translate('high')}`,
      },
    }[seoImpact] || {
      type: 'error',
      text: `${translate('low')}-${translate('high')}`,
    }
  )
}

export const ActionBox = (props) => {
  const {
    type,
    thumbnail,
    heading,
    subHeading,
    time,
    difficulty,
    seoImpact,
    buttonText,
    onClickButton,
  } = props
  const descriptionIcon = type === 'article' ? bookIcon : playIcon
  const description =
    type === 'article' ? translate('how_to_article') : translate('how_to_video')

  const difficultyLabel = getDifficultyLabel(difficulty)
  const seoImpactLabel = getSEOImpactLabel(seoImpact)

  return (
    <Box>
      <Image
        width="100%"
        src={thumbnail}
        onClick={() => onClickButton('thumbnail')}
      />
      <div style={{ padding: '24px' }}>
        <Text
          color={palette.gray}
          fontSize="12px"
          lineHeight="12px"
          fontFamily={fonts.primaryMedium}
          style={{ display: 'flex' }}
        >
          <Icon src={descriptionIcon} margin="0 4px 0 0" />
          {description}
        </Text>
        <Text
          fontSize="14px"
          lineHeight="16px"
          fontFamily={fonts.primaryMedium}
          margin="8px 0 0 0"
        >
          {heading}
        </Text>
        <Text fontSize="16px" lineHeight="28px" margin=" 16px 0 0 0">
          {subHeading}
        </Text>
        <Text
          fontSize="12px"
          lineHeight="12px"
          color={palette.gray}
          margin="16px 0 0 0"
        >
          <Icon src={clockIcon} margin="0 4px 0 0" />
          {`${translate('estimated_time_spent')} ${time}`}
        </Text>
        <div
          style={{
            display: 'flex',
            margin: '8px 0 90px 0',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Text
            fontSize="12px"
            lineHeight="12px"
            color={palette.gray}
            style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Icon src={meterIcon} margin="0 4px 0 0" />
            {translate('video_and_article_difficulty')}
            <ColorLabel type={difficultyLabel.type}>
              {difficultyLabel.text}
            </ColorLabel>
          </Text>
          <Text
            fontSize="12px"
            lineHeight="12px"
            color={palette.gray}
            style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
          >
            <Icon src={graphIcon} margin="0 4px 0 0" />
            {translate('video_and_article_seo_impact')}
            <ColorLabel type={seoImpactLabel.type}>
              {seoImpactLabel.text}
            </ColorLabel>
          </Text>
        </div>
        <PrimaryButton
          theme="orange"
          tag={buttonText || translate('see_how_button')}
          height="48px"
          style={{
            marginTop: '40px',
            position: 'absolute',
            bottom: '24px',
            width: '206px',
            maxWidth: '80%',
          }}
          onClick={() => onClickButton('See How')}
        />
      </div>
    </Box>
  )
}

export const Note = styled.div((props) => ({
  backgroundColor: props.color ? props.color : palette.white,
  color: props.textColor || palette.darkGray,
  boxShadow: '-8px 14px 16px 0px rgba(19,23,35,0.04)',
  flex: 1,
  fontSize: props.small ? '14px' : '16px',
  letterSpacing: '0.05em',
  lineHeight: '20px',
  fontFamily: props.light ? fonts.primaryLight : fonts.primary,
  display: 'flex',
  marginLeft: 0,
  padding: 16,
  alignItems: 'center',
  [mediaQueries.mobile]: {
    fontSize: '12px',
    padding: '10px 15px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginBottom: props.mobileMarginBottom || '8px',
  },
  ...props.style,
}))

export const List = styled.ul({
  padding: '0 0 0 20px',
  width: '100%',
  margin: '10px 0 20px 0',
  listStyle: 'disc',
})

export const Item = styled.li({
  fontSize: '15px',
  fontFamily: fonts.primaryLight,
  color: palette.darkGray,
})

const PositionWrapper = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  [mediaQueries.mobile]: {
    textAlign: 'center',
  },
})

export const ValueChangeInfo = ({
  newValue,
  oldValue,
  change,
  reverse,
  inActive,
  ...props
}) => {
  const isUp =
    (!!newValue && newValue <= oldValue) || (!!newValue && oldValue === null)
  const hasChange = newValue && Number(oldValue - newValue) === change
  const hasArrow = oldValue !== newValue
  const isZero = hasChange && change === 0

  return (
    <PositionWrapper style={props.style}>
      {hasChange && (
        <>
          <Text
            fontSize={props.valueFontSize || '14px'}
            mobileFontSize={props.mobileValueFontSize || '10px'}
            lineHeight="1.2em"
            color={inActive ? palette.lightGray : palette.gray}
            margin="0 5px 0 0"
            mobileMargin="0 2px 0 0"
          >
            {oldValue || ''}
          </Text>
          <Text
            fontSize={props.valueFontSize || '14px'}
            mobileFontSize={props.mobileValueFontSize || '10px'}
            lineHeight="1.2em"
            color={props.arrowColor || palette.gray}
            margin="0 5px 0 0"
            mobileMargin="0 2px 0 0"
          >
            →
          </Text>
        </>
      )}
      <Text
        fontSize={props.valueFontSize || '14px'}
        mobileFontSize={props.mobileValueFontSize || '10px'}
        lineHeight="1.2em"
        fontFamily={change !== 0 ? fonts.primaryBold : fonts.primaryBook}
        margin="0 5px 0 0"
        mobileMargin="0 2px 0 0"
        color={inActive ? palette.gray : palette.darkGray}
      >
        {hasChange ? newValue : '-'}
      </Text>
      {hasChange && hasArrow && (
        <ArrowLabel
          small
          up={reverse ? !isUp : isUp}
          isZero={isZero}
          style={{ marginTop: '2px' }}
        />
      )}
      {change !== null && hasChange && hasArrow && (
        <Text
          fontSize={props.changeFontSize || '14px'}
          mobileFontSize={props.mobileChangeFontSize || '10px'}
          lineHeight="1.3em"
          color={inActive ? palette.gray : palette.darkGray}
        >
          {change > 0 ? `+${change}` : change}
        </Text>
      )}
    </PositionWrapper>
  )
}
